import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { CourseEvaluationState } from 'enum/courseEvaluation-state.enum'
import styles from './EvaluationResults.module.scss'

interface IProps {
  results: {
    approved: number
    failed: number
    total: number
    order: number[]
  }
  inline?: boolean
}

interface data {
  state: CourseEvaluationState
  evaluations: {
    approvedCount: number
    failedCount: number
    total: number
    order?: number[]
  }
}
const initialState: data = {
  state: CourseEvaluationState.inProcess,
  evaluations: {
    approvedCount: 0,
    failedCount: 0,
    total: 0,
    order: [],
  },
}

const EvaluationResults: React.FC<IProps> = ({ results, inline = false }) => {
  const { t } = useTranslation()
  const [
    {
      state,
      evaluations: { approvedCount, failedCount, total, order },
    },
    setData,
  ] = useState(initialState)
  const { approved, failed, inProcess } = CourseEvaluationState
  const prefix = 'administration.monitor'
  useEffect(() => {
    if (results.total)
      setData({
        state:
          results.approved === results.total
            ? approved
            : results.approved + results.failed === results.total &&
              results.failed > 0
            ? failed
            : results.total > 0
            ? inProcess
            : approved,
        evaluations: {
          approvedCount: results.approved,
          failedCount: results.failed,
          total: results.total,
          order: results.order,
        },
      })
  }, [approved, failed, inProcess, results])

  const getPoints = (className: string, index: number) => {
    return (
      <div
        key={`dot-${index}-${className}`}
        className={`${styles.dot} ${className}`}
      />
    )
  }

  const preparePoints = (state: number) => {
    let currentState = ''
    let count = 0
    let grammarNumber = ''
    switch (state) {
      case inProcess:
        currentState = 'inProcess'
        count = total - approvedCount - failedCount
        break
      case approved:
        currentState = 'approved'
        count = approvedCount
        break
      case failed:
        currentState = 'failed'
        count = failedCount
        break
      default:
        break
    }

    grammarNumber = count !== 1 ? 'plural' : 'singular'

    return `${count} ${t(
      `${prefix}.evaluationsCount.evaluation.${grammarNumber}`
    )}              
   ${t(`${prefix}.evaluationsCount.${currentState}.${grammarNumber}`)}`
  }

  if (!total) return <div className="mx-1">-</div>

  return (
    <div
      className={`${styles.evaluationResultsContainer} ${
        inline ? styles.inline : ''
      }`}
    >
      <div className={styles.state}>
        <FontAwesomeIcon
          className={`mr-1 `}
          icon={
            state === approved
              ? faCheckCircle
              : state === failed
              ? faTimesCircle
              : faHourglassHalf
          }
        />
        {t(
          `${prefix}.${
            state === approved
              ? 'approved'
              : state === failed
              ? 'failed'
              : 'inProcess'
          }`
        )}
      </div>
      <div className={styles.evaluations}>
        {total <= 5 ? (
          <div>
            <div className={styles.dots}>
              {order &&
                order.map((state: number, index) =>
                  getPoints(
                    state === inProcess
                      ? styles.inProcess
                      : state === approved
                      ? styles.approved
                      : styles.failed,
                    index
                  )
                )}
              {preparePoints(state)}
            </div>
          </div>
        ) : (
          <div className={styles.inWords}>
            {t(`${prefix}.evaluationsCount.evaluationsAndTotal`, {
              approved: approvedCount,
              total,
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default EvaluationResults
